import { Extension } from '@tiptap/core'

export interface TextDirectionOptions {
  /**
   * The types where the direction attribute can be applied.
   * @default []
   * @example ['heading', 'paragraph']
   */
  types: string[]

  /**
   * The alignments which are allowed.
   * @default ['ltr', 'rtl']
   */
  directions: string[]

  /**
   * The default direction.
   * @default 'ltr'
   * @example 'rtl'
   */
  defaultDirection: string | null
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    textDirection: {
      setTextDirection: (direction: string) => ReturnType
      unsetTextDirection: () => ReturnType
    }
  }
}

export const TextDirection = Extension.create<TextDirectionOptions>({
  name: 'textDirection',

  addOptions() {
    return {
      types: [],
      defaultDirection: 'ltr',
      directions: ['ltr', 'rtl']
    }
  },

  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          direction: {
            default: this.options.defaultDirection,
            parseHTML: (element) => {
              const direction = element.style.direction

              return this.options.directions.includes(direction)
                ? direction
                : this.options.defaultDirection
            },
            renderHTML: (attributes) => {
              if (!attributes.direction || attributes.direction === 'ltr') {
                return {}
              }

              return { style: `direction: ${attributes.direction}` }
            }
          }
        }
      }
    ]
  },

  addCommands() {
    return {
      setTextDirection:
        (direction: string) =>
        ({ commands }) => {
          if (!this.options.directions.includes(direction)) {
            return false
          }

          return this.options.types
            .map((type) => commands.updateAttributes(type, { direction: direction }))
            .every((response) => response)
        },
      unsetTextDirection:
        () =>
        ({ commands }) => {
          return this.options.types
            .map((type) => commands.resetAttributes(type, 'direction'))
            .every((response) => response)
        }
    }
  }
})
